import React, { useEffect, useState } from "react";
import { sortBy } from "lodash";

import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { CategoryCard, TitleText, VariableCard } from "@/components/atoms";
import { useInsightFilters } from "@/context";
import { InsightResult, TitleGenres } from "@/types";

type CategoryFilter = "Movie" | "Show" | "Sports";
type ContentType = "Series" | "Movie" | "Show" | "Sports";
const CATEGORY_MAP: Record<CategoryFilter, readonly ContentType[]> = {
  "Movie": ["Movie"],
  "Show": ["Series", "Show"],
  "Sports": ["Sports"]
} as const;

interface ContentViewProps {
  scores: InsightResult[];
  lkp?: TitleGenres[];
}

export const ContentView: React.FC<ContentViewProps> = ({ scores, lkp }) => {
  const {
    filters: { scoreType },
  } = useInsightFilters();

  const [selectedType, setSelectedType] = useState<string>('');
  const typeText = selectedType.replace(/(?<!s)$/i, 's');
  const [selectedGenre, setSelectedGenre] = useState<string>('');
  const [genres, setGenres] = useState<InsightResult[]>([]);
  const [titles, setTitles] = useState<InsightResult[]>([]);
  const [minMax, setMinMax] = useState<number[]>([0, 1]);

  useEffect(() => {
    setSelectedType('Movie');
  }, []);

  useEffect(() => {
    if (selectedType === 'Movie') {
      const update = sortBy(scores.filter(x => x.tag_category === 'Movie Genres'), scoreType.value).reverse()
      setGenres(update);
      setSelectedGenre(update[0].tag_name);
    } else if (selectedType === 'Show') {
      const update = sortBy(scores.filter(x => x.tag_category === 'TV Genres'), scoreType.value).reverse()
      setGenres(update);
      setSelectedGenre(update[0].tag_name);
    } else {
      setGenres([]);
      setSelectedGenre('Sports');
    }
  }, [scores, selectedType, scoreType.value]);

  useEffect(() => {
    if (!lkp) {
      setTitles([]);
      return;
    }

    const targetTypes = new Set(CATEGORY_MAP[selectedType as keyof typeof CATEGORY_MAP]);
    const allowedTitles = new Set(lkp.filter(x =>
      x.genre === selectedGenre && targetTypes.has(x.content_type as ContentType)).map(x => x.title));
    setTitles(sortBy(scores.filter(x => allowedTitles.has(x.tag_name)), scoreType.value).reverse());
  }, [lkp, scores, selectedGenre, selectedType, scoreType.value])

  useEffect(() => {
    const maxScore = Math.max.apply(
      Math,
      scores
        .filter((x) => x.tag_name !== x.tag_category)
        .map((x) => Number(x[scoreType.value as keyof InsightResult])),
    );
    const minScore =
      scoreType.value === "rate"
        ? Math.min.apply(
          Math,
          scores
            .filter((x) => x.tag_name !== x.tag_category)
            .map((x) => Number(x[scoreType.value as keyof InsightResult])),
        )
        : Math.max(1 - (maxScore - 1), 0);

    setMinMax([minScore, maxScore])
  }, [scores, scoreType])

  const handleTypeSelect = (contentType: string) => {
    setSelectedType(contentType);
  }

  const handleGenreSelect = (genre: string) => {
    setSelectedGenre(genre);
  }

  return (
    <>
      <Grid size={12}>
        <Typography>What types of content do they watch?</Typography>
      </Grid>
      <Grid container size={12} spacing={2}>
        {scores.filter(x => x.tag_category === 'Content Type').map((item, index) => (
          <CategoryCard
            key={`content-type-${index}`}
            category={item.tag_name}
            value={Number(item[scoreType.value as keyof InsightResult])}
            score={scoreType.value}
            type='tv'
            minValue={minMax[0]}
            maxValue={minMax[1]}
            selectedCategory={selectedType}
            onSelect={handleTypeSelect}
          />
        ))}
      </Grid>
      {genres.length > 0 && (
        <>
          <Grid size={12} mt={2}>
            <Typography>{`What ${selectedType} Genres do they prefer?`}</Typography>
          </Grid>
          <Grid container size={12} spacing={1.5} height={380} sx={{ overflowY: 'auto' }}>
            {genres.map((item, index) => (
              <CategoryCard
                key={`genre-${index}`}
                category={item.tag_name}
                value={Number(item[scoreType.value as keyof InsightResult])}
                score={scoreType.value}
                type="tv"
                minValue={minMax[0]}
                maxValue={minMax[1]}
                selectedCategory={selectedGenre}
                onSelect={handleGenreSelect}
                width={218}
              />
            ))}
          </Grid>
        </>
      )}
      {titles.length > 0 && (
        <>
          <Grid size={12} mt={2}>
            <TitleText
              text={`What ${selectedGenre} ${selectedGenre === 'Sports' ? '' : typeText} do they Watch?`}
              subtext={`Not all ${typeText} will be shown here, this is just a subset of the most popular ${typeText}. ${typeText} can have multiple Genres.`}
              variant="body1"
              spacing={0.5}
            />
          </Grid>
          <Grid container size={12} spacing={1.5}>
            {titles.map((item, index) => (
              <Grid key={`title-${index}`}>
                <VariableCard
                  name={item.tag_name}
                  value={Number(item[scoreType.value as keyof InsightResult])}
                  score={scoreType.value}
                  minValue={minMax[0]}
                  maxValue={minMax[1]}
                  width={218}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

    </>
  );
};

export default ContentView;
